import { useState } from "react";

import { COPY } from "../../copy";
import { getAdAcquiredCustomerValueKpis } from "../../customersData";
import type { InsightsChartMetric, InsightsCustomerSegment } from "../../types";
import {
  useCardPaddingClass,
  useIsCompact,
  useStackGapClass,
} from "../CardDensity";
import { ExpandCardButton } from "../ExpandCardButton";
import { OverviewKpiGrid } from "../overview/OverviewKpiGrid";
import { PerformanceByStoreTypeChart } from "../PerformanceByStoreTypeChart";
import { StoreTypePerformanceTable } from "../StoreTypePerformanceTable";

/**
 * Store-type insights on the Customers tab: the monthly trend and the
 * customer-value metrics form the left rail; the cumulative table spans both
 * rows on the right. V3 puts the metrics tile above the chart; V1/V2 keep it
 * below.
 *
 * Ad type comes from the tab. Date range belongs only to the cumulative table;
 * the chart pages its complete monthly history with arrows.
 *
 * The table is given the wider column because it carries seven columns to the
 * chart's plot-plus-legend. Below 1440px even 7/12 of the page is too narrow for
 * those columns, so the pair stacks and each card gets the full width.
 *
 * Store-type targeting/boosting was removed from Ads Manager 2.0, so both
 * always render the unboosted view (top store types).
 */

/** Which card, if any, has been given the full page width. */
type ExpandedCard = "chart" | "table";

/**
 * The table card's height comes from the left rail beside it, so its page size
 * is whatever fills that height at the 60px shared row height: seven rows beside
 * the chart plus the customer-value card, four beside the chart alone. Fewer
 * leaves the bottom of the card empty; more pushes the row taller than the
 * chart and pads the rail with whitespace instead.
 */
const ROWS_WITH_CUSTOMER_VALUE_CARD = 7;
const ROWS_WITHOUT_CUSTOMER_VALUE_CARD = 4;

/**
 * V3's rail lists the top six store types and pages the rest. Six is what the
 * 52px rows fit beside the condensed plot without pushing the rail taller than
 * the column of cards opposite it.
 */
const DENSE_ROWS = 6;

/** Expanded, the table owns the full width and sets its own height. */
const ROWS_WHEN_EXPANDED = 10;

export function CustomerInsightsSection({
  customerSegment,
  useTotalAdSales = false,
  defaultChartMetric = "ad-sales",
  customerValueAboveChart = false,
}: {
  customerSegment: InsightsCustomerSegment;
  useTotalAdSales?: boolean;
  defaultChartMetric?: InsightsChartMetric;
  /** V3: the metrics tile sits above the store-type chart instead of below it. */
  customerValueAboveChart?: boolean;
}) {
  const [chartMetric, setChartMetric] =
    useState<InsightsChartMetric>(defaultChartMetric);
  const cardPaddingClass = useCardPaddingClass();
  const stackGapClass = useStackGapClass();
  const isCompact = useIsCompact();
  // Expanding is a focus move, not just a resize: the sibling card comes out of
  // the row rather than dropping below it, because leaving it there would give
  // the reader two full-width cards and no sense of which one they expanded.
  // Collapsing puts the pair back, so nothing is lost.
  const [expanded, setExpanded] = useState<ExpandedCard | null>(null);

  const toggle = (card: ExpandedCard) =>
    setExpanded((current) => (current === card ? null : card));

  const chartExpanded = expanded === "chart";
  const tableExpanded = expanded === "table";
  const showCustomerValue = customerSegment !== "returning-customers";
  const customerValueKpis = getAdAcquiredCustomerValueKpis({
    showInputs: isCompact,
  });
  const rowsPerPage = tableExpanded
    ? ROWS_WHEN_EXPANDED
    : isCompact
      ? DENSE_ROWS
      : showCustomerValue
        ? ROWS_WITH_CUSTOMER_VALUE_CARD
        : ROWS_WITHOUT_CUSTOMER_VALUE_CARD;

  const customerValueCard = showCustomerValue ? (
    <div
      className={`bg-fs-surface-primary rounded-lg border border-[#dfe0e1] ${cardPaddingClass}`}
    >
      {customerValueAboveChart ? null : (
        <h3 className="mb-4 text-sm leading-5 font-medium text-[#333]">
          {COPY.customers.customerValueCardTitle}
        </h3>
      )}
      <OverviewKpiGrid items={customerValueKpis} columns={2} />
    </div>
  ) : null;

  return (
    <div
      className={
        expanded == null
          ? `grid grid-cols-1 ${stackGapClass} min-[1440px]:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]`
          : `grid grid-cols-1 ${stackGapClass}`
      }
    >
      {!tableExpanded ? (
        <div className={`flex min-w-0 flex-col ${stackGapClass}`}>
          {customerValueAboveChart ? customerValueCard : null}
          <PerformanceByStoreTypeChart
            hasBoosting={false}
            selectedStoreTypes={[]}
            customerSegment={customerSegment}
            storeTypeGrouping="top"
            chartMetric={chartMetric}
            onChartMetricChange={setChartMetric}
            expanded={chartExpanded}
            headerAction={
              <ExpandCardButton
                expanded={chartExpanded}
                onClick={() => toggle("chart")}
              />
            }
          />
          {customerValueAboveChart ? null : customerValueCard}
        </div>
      ) : null}
      {!chartExpanded ? (
        <div className="h-full min-w-0">
          <StoreTypePerformanceTable
            hasBoosting={false}
            selectedStoreTypes={[]}
            customerSegment={customerSegment}
            useTotalAdSales={useTotalAdSales}
            onOpenStoreTypesModal={() => undefined}
            rowsPerPage={rowsPerPage}
            headerAction={
              <ExpandCardButton
                expanded={tableExpanded}
                onClick={() => toggle("table")}
              />
            }
          />
        </div>
      ) : null}
    </div>
  );
}
